<template>
  <v-container fluid class="view-reports__container pa-0">
    <v-row class="view-reports__row">
      <v-col cols="6" class="pa-0">
        <div v-if="selectedScenario" class="h-100 d-flex flex-column flex-grow-1">
          <h2 class="view-reports__heading mb-3">
            {{ tkey('header') }}
          </h2>
          <AgGridVue
            class="w-75 h-100"
            :column-defs="headers"
            :row-data="reports"
            :grid-options="gridOptions"
            @grid-ready="onGridReady"
          />
        </div>
        <section v-else>
          <h2>{{ $t('errors.scenarioNotFound') }}</h2>
        </section>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
// Composables
import { useInternationalization } from '@/composables/use-internationalization';
import { useDayjs } from '@/composables/use-dayjs';

// Stores
import { useContextStore } from '@/stores/context';
import { useScenariosStore } from '@/stores/scenarios';
import { useSimpleSwapRunsStore } from '@/stores/simple-swap-runs';

import agGridUtils from '@/utils/ag-grid-utils';
import jobapi from '@enums/jobapi';
import { get, isNil, map } from 'lodash';
import { mapActions, mapState } from 'pinia';
import AgGridCellRendererComponentWrapper from '@/components/AgGridCellRendererComponentWrapper.vue';
import AgGridIcon from '@/components/AgGridIcon.vue';

export default {
  setup() {
    const { tkey } = useInternationalization('viewReportsPage');
    const { getUTCFromDate } = useDayjs();
    return { tkey, getUTCFromDate };
  },
  data() {
    return {
      gridApi: null,
      gridOptions: {
        columnMenu: 'legacy',
        suppressContextMenu: true,
        defaultColDef: {
          filter: true,
          sortable: true,
          comparator: agGridUtils.sortings.naturalSort,
          editable: false,
          menuTabs: ['filterMenuTab'],
          suppressMovable: true,
        },
        rowHeight: 41,
        headerHeight: 40,
      },
      actionColDef: {
        ...agGridUtils.colDefs.action,
        menuTabs: [],
      },
    };
  },

  computed: {
    ...mapState(useScenariosStore, ['selectedScenario']),
    ...mapState(useSimpleSwapRunsStore, ['scenarioSimpleSwapRuns']),
    ...mapState(useContextStore, ['getDateFormats', 'getCurrentTenant']),

    headers() {
      return [
        {
          headerName: this.tkey('tableHeaders.runDescription'),
          field: 'runDescription',
          cellClass: 'font-weight-bold',
          flex: 1,
        },
        {
          headerName: this.tkey('tableHeaders.date'),
          field: 'extractDatetime',
          flex: 1,
        },
        {
          headerName: this.tkey('tableHeaders.maxNumberSwaps'),
          field: 'maxSwaps',
          flex: 1,
        },
        {
          ...this.actionColDef,
          headerName: this.tkey('tableHeaders.status'),
          field: 'extractStatus',
          width: 75,
          sortable: true,
          comparator: (valueA, valueB) => {
            return valueB.localeCompare(valueA, undefined, { sensitivity: 'base' });
          },
          cellRenderer: AgGridIcon,
          // Send the name of the icon using valueGetter. This is used in the component as this.params.value
          valueGetter: params => jobapi.jobStatusesIconsMappings[params.data.status],
        },
        {
          ...this.actionColDef,
          field: 'extractDownload',
          width: 40,
          cellClass: 'justify-center',
          cellClassRules: { 'v-icon--disabled': params => isNil(params.data.fileId) },
          cellRenderer: AgGridCellRendererComponentWrapper,
          // ValueGetter should return an object with the component name to use and the props it should receive.
          // The agGridCellRendererComponentWrapper will then handle rendering this component and passing these props to it
          valueGetter: params => {
            const tenant = this.getCurrentTenant;
            const baseUrl = `/api/file/${params.data.fileId}?type=excel`;
            const url = tenant ? `${baseUrl}&tenant=${tenant}` : baseUrl;

            return {
              component: 'base-download-link',
              props: {
                url,
                filename: params.data.fileName,
                visible: !isNil(params.data.fileId),
              },
            };
          },
        },
      ];
    },

    reports() {
      return map(this.scenarioSimpleSwapRuns, e => {
        const date = get(e, ['jobs', 'simpleSwaps', 'dateTimeComplete']);
        return {
          runDescription: e.runDescription,
          extractDatetime: date
            ? this.getUTCFromDate(date).format(this.getDateFormats.longWithTime)
            : null,
          maxSwaps: e.maxSwaps,
          status: get(e, ['jobs', 'simpleSwaps', 'status'], jobapi.jobStatuses.failed),
          fileId: e.gridFsExcelFileId,
          fileName: e.runDescription,
        };
      });
    },
  },

  created() {
    this.fetchSimpleSwapRuns();
  },

  methods: {
    ...mapActions(useSimpleSwapRunsStore, ['fetchSimpleSwapRuns']),

    onGridReady(params) {
      this.gridApi = params.api;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.view-reports {
  &__container {
    flex: 1;
    height: 100%;
  }

  &__row {
    height: 100%;
  }

  &__heading {
    color: $assortment-table-header-colour;
    font-size: 1.4rem;
  }
}

:deep(.ag-theme-alpine) {
  .ag-root-wrapper {
    background-color: transparent;
  }

  .ag-row {
    background-color: $assortment-table-white-bg-colour;
    border-top: 5px solid $assortment-background;
  }

  .ag-header-row,
  .ag-row {
    color: $assortment-text-colour;
    line-height: 1.5rem;
  }

  .ag-header-cell,
  .ag-cell {
    padding: 5px;
  }
}
</style>
